/* eslint-disable react-hooks/exhaustive-deps */
import {
	Button,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	Box,
	Flex,
	useToast,
	Switch,
	Text,
	IconButton,
	Stack,
	Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { useMemo, useState, useRef } from 'react';
import { gql, useMutation } from '@apollo/client';
import { FaUndoAlt } from 'react-icons/fa';
import { MdDelete, MdCleaningServices } from 'react-icons/md';
import { getSignSpecifications } from '../utils';
import TextFormField from '../../../components/form/TextFormField';
import SelectValueFormField from '../../../components/form/SelectValueFormField';
import TextAreaFormField from '../../../components/form/TextAreaFormField';
import FileFormField from '../../../components/form/FileFormField';
import CanvasDraw from 'react-canvas-draw';
import c from '../../../constant';
import * as Yup from 'yup';

const M_SIGN_NP = gql`
	mutation signNp(
		$base64draw: String
		$unit: String
		$title: String
		$other: JSON
		$attachmentFileSize: Float
		$attachmentFile: Upload
		$decision: String
		$signId: String!
	) {
		signNp(
			base64draw: $base64draw
			unit: $unit
			title: $title
			other: $other
			attachmentFileSize: $attachmentFileSize
			attachmentFile: $attachmentFile
			decision: $decision
			signId: $signId
		) {
			id
			createdAt
			userCreatedBy {
				id
				username
				fullName
				fullNameKhmer
				avatar
			}
			createdBy
			position
			reviewer
			userReviewer {
				id
				username
				fullName
				fullNameKhmer
				avatar
			}
			decision
			decisionAt
			signature
			decisionFile
		}
	}
`;

const maxFileSize = 5242880;

function Create({ signData = null, onClose = () => null, onComplete = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const [createRecord, { loading }] = useMutation(M_SIGN_NP);

	const canvasDrawRef = useRef(null);
	const [isDraw, setIsDraw] = useState(true);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				title: Yup.string().required('required'),
				unit: Yup.string().required('required'),
				decision: Yup.string().nullable(),
				attachment: Yup.mixed().test('fileSize', 'over limit', (value) => {
					if (value) {
						return value?.size <= maxFileSize;
					} else {
						return true;
					}
				}),
			}),
		[signData]
	);
	const specification = useMemo(() => getSignSpecifications(signData?.userReviewer, signData?.position), [signData]);

	return (
		<Modal size="xl" closeOnEsc={!loading} closeOnOverlayClick={!loading} isOpen={true} onClose={onClose}>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader pb={0} mb={0}>
					{t('Sign')}
				</ModalHeader>
				<ModalCloseButton isDisabled={loading} />
				<ModalBody>
					<Formik
						initialValues={{
							title: signData?.signature?.title || '',
							unit: specification?.unit || '',
							decision: '',
							attachment: null,
						}}
						validationSchema={validationSchema}
						onSubmit={(values) => {
							const base64draw = canvasDrawRef?.current?.getDataURL();

							const title = values?.title || '';
							const unit = values?.unit || '';
							const decision = values?.decision || '';
							const attachmentFile = values?.attachment || null;
							const attachmentFileSize = values?.attachment?.size || 0;
							const variables = {
								signId: signData?.id,
								title,
								unit,
								decision,
								attachmentFile,
								attachmentFileSize,
								base64draw,
							};
							createRecord({ variables })
								.then(({ data }) => {
									onClose();
									onComplete(data?.signNp);
								})
								.catch((error) => {
									if (error?.graphQLErrors?.length > 0) {
										error?.graphQLErrors.map(({ message }, i) =>
											toast({
												title: t(message),
												status: 'error',
												isClosable: true,
												position: 'bottom-right',
											})
										);
									} else {
										toast({
											title: t(error?.message ? error?.message : 'something went wrong'),
											status: 'error',
											isClosable: true,
											position: 'bottom-right',
										});
									}
								});
						}}
					>
						{({ values }) => (
							<Form>
								<Box mt="2">
									<>
										<SelectValueFormField
											placeholder={t('title')}
											name="title"
											label="title"
											options={specification?.titles}
											required={true}
										/>
									</>
									<>
										<TextFormField name="unit" label={specification?.unitTitle} disabled={true} required={true} />
									</>
									<>
										{signData?.position === 'Director General' && (
											<>
												<Flex>
													<Text fontSize="md">{t(signData?.labelComment)}</Text>
												</Flex>
												<Flex>
													<Text fontSize="md">{t('Write')}</Text>
													<Switch id="draw-input" ml={4} mr={4} onChange={() => setIsDraw(!isDraw)} />
													<Text fontSize="md">{t('Input')}</Text>
												</Flex>
												<>
													{isDraw && (
														<Flex flexDirection="column" justifyContent="center" alignItems="center" mt={4} mb={4}>
															<Stack direction="row-reverse" spacing={2} width={360} mb="4px">
																<Tooltip label={t('Clear')}>
																	<IconButton
																		variant="outline"
																		width="30px"
																		height="30px"
																		borderColor="gray.200"
																		icon={<MdDelete size={18} color="gray" />}
																		onClick={() => canvasDrawRef?.current?.clear()}
																	/>
																</Tooltip>
																<Tooltip label={t('Clean')}>
																	<IconButton
																		variant="outline"
																		width="30px"
																		height="30px"
																		borderColor="gray.200"
																		icon={<MdCleaningServices size={16} color="gray" />}
																		onClick={() => canvasDrawRef?.current?.eraseAll()}
																	/>
																</Tooltip>
																<Tooltip label={t('Undo')}>
																	<IconButton
																		variant="outline"
																		width="30px"
																		height="30px"
																		borderColor="gray.200"
																		icon={<FaUndoAlt size={14} color="gray" />}
																		onClick={() => canvasDrawRef?.current?.undo()}
																	/>
																</Tooltip>
															</Stack>
															<Box borderWidth={1} borderColor="gray.200" borderRadius={2}>
																<CanvasDraw
																	ref={canvasDrawRef}
																	canvasWidth={360}
																	canvasHeight={540}
																	brushColor={c.penColor}
																	brushRadius={1}
																	lazyRadius={0}
																/>
															</Box>
														</Flex>
													)}
													{!isDraw && (
														<>
															{signData?.canComment && (
																<>
																	<TextAreaFormField name="decision" label={null} required={false} />
																</>
															)}
														</>
													)}
												</>
											</>
										)}
									</>
									<>
										{signData?.position !== 'Director General' && (
											<>
												{signData?.canComment && (
													<>
														<TextAreaFormField name="decision" label={signData?.labelComment} required={false} />
													</>
												)}
											</>
										)}
									</>
									<>
										{signData?.canDelegate && (
											<>
												<FileFormField name="attachment" label="Delegation Document" required={false} />
											</>
										)}
									</>
									<>
										<Flex flex="1" mt="6" mb={2} justifyContent="flex-end" alignItems="center">
											<Button type="submit" isLoading={loading} borderRadius={c.borderRadius} colorScheme="blue" onMouseDown={() => null}>
												{t('Sign')}
											</Button>
										</Flex>
									</>
								</Box>
							</Form>
						)}
					</Formik>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default Create;
